import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { evaluateMinMaxItmTop5 } from './MinMaxItmTop5';
import { evaluateConnectingDotsItmTop5 } from './ConnectingDotsItmTop5';

/**
 * CombinedMinMaxAndDots — Combined MIN/MAX ITM Top5 + Connecting Dots (Top 5 ITM)
 *
 * This strategy evaluates both the MIN/MAX Top 5 strategy and Connecting Dots Top 5 ITM strategy.
 * It uses a Safe OR confluence logic:
 * - If one triggers, take the trade.
 * - If both trigger in the SAME direction, take the trade.
 * - If BOTH trigger but they conflict (one BULL, one BEAR), VETO the trade.
 */
export const evaluateCombinedMinMaxAndDots: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    // Evaluate the underlying strategies (extract first leg if array)
    const rawMinMaxSignal = evaluateMinMaxItmTop5(data, currentUnixTime, config, context);
    const rawDotsSignal = evaluateConnectingDotsItmTop5(data, currentUnixTime, config, context);
    
    const minMaxSignal = rawMinMaxSignal ? (Array.isArray(rawMinMaxSignal) ? rawMinMaxSignal[0] : rawMinMaxSignal) : null;
    const dotsSignal = rawDotsSignal ? (Array.isArray(rawDotsSignal) ? rawDotsSignal[0] : rawDotsSignal) : null;

    // If NEITHER produces a signal, do nothing
    if (!minMaxSignal && !dotsSignal) {
        return null;
    }

    // ── THE SAFETY VETO ──
    // If BOTH triggered but they are fighting (one says BULL, one says BEAR), stay out of the market.
    if (minMaxSignal && dotsSignal && minMaxSignal.trade_type !== dotsSignal.trade_type) {
        return null;
    }

    // At this point, either one strategy fired, or both fired in the exact same direction.
    const activeSignal = minMaxSignal || dotsSignal;

    // Build the remarks based on who triggered the trade
    let combinedRemarks = '';
    if (minMaxSignal && dotsSignal) {
        combinedRemarks = `CONFLUENCE (${activeSignal!.trade_type}): MinMax [${minMaxSignal.remarks}] & Dots [${dotsSignal.remarks}]`;
    } else if (minMaxSignal) {
        combinedRemarks = `MINMAX TRIGGER (${minMaxSignal.trade_type}): ${minMaxSignal.remarks}`;
    } else if (dotsSignal) {
        combinedRemarks = `DOTS TRIGGER (${dotsSignal.trade_type}): ${dotsSignal.remarks}`;
    }

    return {
        trade_type: activeSignal!.trade_type,
        strike: activeSignal!.strike,
        option_type: activeSignal!.option_type,
        entry_price: activeSignal!.entry_price,
        remarks: combinedRemarks
    };
};
